import React, { useEffect, useRef, useState } from 'react';
import { Lock, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useNoIndex } from '@/hooks/useNoIndex';

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/portfolio`;

const Portfolio = () => {
  useNoIndex();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ password }),
      });

      if (!res.ok) {
        let message = 'Zugriff verweigert.';
        try {
          const data = await res.json();
          message = data?.error ?? message;
        } catch {
          /* ignore */
        }
        setError(message);
        return;
      }

      // Das Dokument kommt als vollständiges HTML vom Server und wird in einem
      // isolierten iframe dargestellt — unverändert, ohne Tailwind-Einfluss.
      const html = await res.text();
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      urlRef.current = url;
      setDocUrl(url);
    } catch {
      setError('Verbindung zum Server fehlgeschlagen. Bitte später erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  if (docUrl) {
    return (
      <iframe
        src={docUrl}
        title="Portfolio Paweł Czechowski"
        className="fixed inset-0 w-full h-full border-0"
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 py-20">
      <div className="max-w-md w-full">
        <div className="rounded-xl p-8 border border-purple-500/20 bg-[#121212]/80">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-purple-600/20 flex items-center justify-center mb-4">
              <Lock className="text-purple-400" size={24} />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-wide">
              Portfolio — passwortgeschützt
            </h1>
            <p className="text-gray-400 mt-2 text-sm">
              Dieser Bereich ist privat. Bitte geben Sie das Passwort ein, um das Portfolio zu
              sehen.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Passwort"
              autoFocus
              aria-label="Passwort"
              className="bg-black/40 border-purple-500/30 text-white"
            />
            {error && (
              <p className="text-sm text-red-400" role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading || !password}
              className="w-full bg-purple-600 hover:bg-purple-500 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird geprüft …
                </>
              ) : (
                'Portfolio öffnen'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
